import isEmpty from 'lodash/isEmpty';
import { createFrozenMap } from '@/vuex/utils';
import {
    fetchMetaGroups,
    fetchMetaGroup,
    localizeMetaGroups,
    createMetaGroup as createMetaGroupApi,
    addCategoryToMetaGroup as addCategoryToMetaGroupApi,
    removeCategoryFromMetaGroup as removeCategoryFromMetaGroupApi,
} from './meta-groups.utils';

const metaGroups = {
    namespaced: true,
    state: {
        map: {},
        fetching: false,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setMapAtKey (state, { metaGroup }) {
            state.map[metaGroup.id] = metaGroup;
        },
    },
    actions: {
        async loadAll ({ commit, state }, { reFetch = false, items = true } = {}) {
            if (!isEmpty(state.map) && !reFetch) {
                return;
            }

            commit('setFetching', { fetching: true });

            try {
                const { data: metaGroups } = await fetchMetaGroups({ items });
                const localizedMetaGroups = localizeMetaGroups(metaGroups);
                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(localizedMetaGroups),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async loadOne ({ commit, state }, { id, reFetch = false, items = true } = {}) {
            if (!reFetch && state.map[id]) {
                return state.map[id];
            }

            try {
                const { data: metaGroup } = await fetchMetaGroup({ id, items });
                const [localized] = localizeMetaGroups([metaGroup]);
                commit('setMapAtKey', {
                    metaGroup: Object.freeze(localized),
                });

                return localized;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async createMetaGroup ({ commit }, payload = {}) {
            try {
                // The /category endpoint returns an array of affected entities; pluck the metagroup.
                const { data } = await createMetaGroupApi(payload);
                const metaGroup = Array.isArray(data)
                    ? data.find((entity) => entity.type === 'metaGroup')
                    : data;

                commit('setMapAtKey', {
                    metaGroup: Object.freeze(metaGroup),
                });

                return metaGroup;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async addCategoryToMetaGroup ({ dispatch }, { metaGroupId, categoryId }) {
            const response = await addCategoryToMetaGroupApi({ metaGroupId, categoryId });
            // Refresh the metagroup so its cached members stay in sync. Best-effort: the
            // PATCH already succeeded, so a failed refresh shouldn't fail the action.
            await dispatch('loadOne', { id: metaGroupId, reFetch: true, items: true })
                .catch((error) => console.error('Failed to refresh metagroup after add:', error));

            return response;
        },
        async removeCategoryFromMetaGroup ({ dispatch }, { metaGroupId, categoryId }) {
            const response = await removeCategoryFromMetaGroupApi({ metaGroupId, categoryId });
            await dispatch('loadOne', { id: metaGroupId, reFetch: true, items: true })
                .catch((error) => console.error('Failed to refresh metagroup after remove:', error));

            return response;
        },
    },
    getters: {
        list: (state) => Object.values(state.map),
    },
};

export default metaGroups;
